import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

import { formatToLongDate } from "../../../utils/helpers.ts";
import { annualAmountToRoundedMonthly, appraisalCompensationSummary } from "./appraisal-annexure-utils.ts";
import appraisalLetterTemplates from "./appraisal-letter-templates.json";
import type { AppraisalAnnexureValues, EmployeeAppraisalValues } from "./types.ts";

type PersonalDetail = {
  empName?: string;
  firstName?: string;
  designation?: string;
  currentLocation?: string;
};

export type AppraisalLetterPreviewData = {
  employeeName: string;
  salutation: string;
  personalDetail?: PersonalDetail | null;
  basicValues: EmployeeAppraisalValues;
  annexureValues: AppraisalAnnexureValues;
  compensationSummary?: ReturnType<typeof appraisalCompensationSummary>;
};

export function appraisalLetterPdfFilename(employeeName: string): string {
  return `Appraisal_Letter_${employeeName.trim().replace(/\s+/g, "_")}.pdf`;
}

const number = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });

function appraisalRecipientName(data: AppraisalLetterPreviewData): string {
  const firstName = data.personalDetail?.firstName?.trim() ?? "";
  const fullName = data.employeeName.trim() || data.personalDetail?.empName?.trim() || "-";
  return firstName.length > 2 ? firstName : fullName;
}

function numberToIndianWords(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "Zero Only";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const belowThousand = (amount: number): string => {
    const parts: string[] = [];
    if (amount >= 100) { parts.push(`${ones[Math.floor(amount / 100)]} Hundred`); amount %= 100; }
    if (amount >= 20) { parts.push(tens[Math.floor(amount / 10)]); amount %= 10; }
    if (amount > 0) parts.push(ones[amount]);
    return parts.join(" ");
  };
  let remaining = Math.round(value);
  const parts: string[] = [];
  for (const [unit, size] of [["Crore", 10_000_000], ["Lakh", 100_000], ["Thousand", 1_000]] as const) {
    if (remaining >= size) {
      const count = Math.floor(remaining / size);
      parts.push(`${belowThousand(count)} ${unit}`);
      remaining %= size;
    }
  }
  if (remaining) parts.push(belowThousand(remaining));
  return `${parts.join(" ")} Only`;
}

function fieldValue(expression: string, data: AppraisalLetterPreviewData): string | undefined {
  const { basicValues, annexureValues, personalDetail } = data;
  const summary = data.compensationSummary ?? appraisalCompensationSummary(annexureValues);
  const c = summary.components;
  const designation = basicValues.letterType !== "APPRAISAL"
    ? basicValues.designation || personalDetail?.designation
    : personalDetail?.designation;
  const fields: Array<[string, string]> = [
    ["fullName", data.employeeName || personalDetail?.empName || "-"],
    ["letterIssueDate", formatToLongDate(basicValues.letterIssueDate, "-")],
    ["salutation", appraisalRecipientName(data)],
    ["effectiveDate", formatToLongDate(basicValues.effectiveDate, "-")],
    ["designationChangeIfAny", basicValues.designation || personalDetail?.designation || "-"],
    ["designationChange26", basicValues.letterType !== "APPRAISAL" ? "Yes" : "No"],
    ["designation", designation || "-"],
    ["location", personalDetail?.currentLocation || "-"],
    ["basicAnnual", number.format(c.basic * 12)], ["basic", number.format(c.basic)],
    ["pfEmployeePartAnnual", number.format(c.pfEmployeePart * 12)], ["pfEmployeePart", number.format(c.pfEmployeePart)],
    ["specialAllowanceAnnual", number.format(c.specialAllowance * 12)], ["specialAllowance", number.format(c.specialAllowance)],
    ["houseRentAllowanceAnnual", number.format(c.houseRentAllowance * 12)], ["houseRentAllowance", number.format(c.houseRentAllowance)],
    ["internetReimbAnnual", number.format(c.internetReimbursement * 12)], ["internetReimb", number.format(c.internetReimbursement)],
    ["totalABCAnnual", number.format(summary.annualGross)], ["totalABC", number.format(summary.monthlyGross)],
    ["gratuityAnnual", number.format(Math.ceil(c.basic / 2))], ["gratuity", number.format(c.gratuity)],
    ["pfEmployerPartAnnual", number.format(c.pfEmployerPart * 12)], ["pfEmployerPart", number.format(c.pfEmployerPart)],
    ["totalDAnnual", number.format(summary.annualBenefits)], ["totalD", number.format(summary.monthlyBenefits)],
    ["annualFixedComponentEAnnual", number.format(c.annualFixedComponent)], ["annualFixedComponentE", number.format(annualAmountToRoundedMonthly(c.annualFixedComponent))],
    ["annualGrossCTCInWords", numberToIndianWords(summary.annualCtc)],
    ["annualGrossCTCAnnual", number.format(summary.annualCtc)], ["annualGrossCTC", number.format(summary.monthlyCtc)],
    ["annualComponentDate", "Annual Fixed component shall be paid at the completion of 1 year."],
    ["noticePeriod", basicValues.noticePeriod || "Ninety (90) days"],
  ];
  return fields.find(([key]) => expression.includes(`promotionDetails.${key}`))?.[1];
}

export async function createAppraisalLetterHtml(data: AppraisalLetterPreviewData, includeToolbar = false): Promise<string> {
    const publicUrl = process.env.PUBLIC_URL ?? "";
    const templateBase = new URL(`${publicUrl.replace(/\/$/, "")}/templates/`, window.location.origin).toString();
    const parseTemplate = (html: string): Document => new DOMParser().parseFromString(html, "text/html");
    const isAppraisalTemplate = (documentNode: Document): boolean => Boolean(
      documentNode.querySelector("body > .header") &&
      documentNode.querySelector("body > .footer") &&
      documentNode.querySelector("body > .page .letter-content") &&
      documentNode.querySelector("body > .page .annexure-content"),
    );
    let documentNode = parseTemplate(appraisalLetterTemplates.dynamic);
    if (!isAppraisalTemplate(documentNode)) {
      documentNode = parseTemplate(appraisalLetterTemplates.fallback);
    }
    if (!isAppraisalTemplate(documentNode)) {
      throw new Error("The production server returned the application page instead of the appraisal letter template.");
    }
    const base = documentNode.createElement("base");
    base.href = templateBase;
    documentNode.head.prepend(base);
    const stylesheet = documentNode.querySelector<HTMLLinkElement>('link[href*="appraisal-v2-dynamic.css"]')
      ?? documentNode.createElement("link");
    stylesheet.rel = "stylesheet";
    stylesheet.href = `${templateBase}appraisal-v2-dynamic.css?v=20260819-2`;
    if (!stylesheet.parentNode) documentNode.head.appendChild(stylesheet);
    const pagination = documentNode.querySelector<HTMLScriptElement>('script[src*="appraisal-v2-dynamic.js"]')
      ?? documentNode.createElement("script");
    pagination.src = `${templateBase}appraisal-v2-dynamic.js?v=20260813-3`;
    pagination.defer = false;
    if (!pagination.parentNode) documentNode.body.appendChild(pagination);
    const hasDesignationChange = data.basicValues.letterType !== "APPRAISAL";

    documentNode.querySelectorAll("*").forEach((element) => {
      const condition = element.getAttribute("th:if");
      if (condition) {
        if (condition.includes("promotionDetails.annualComponent")) return;
        const negated = condition.includes("!");
        if ((negated && hasDesignationChange) || (!negated && !hasDesignationChange)) element.remove();
      }
    });
    documentNode.querySelectorAll("*").forEach((element) => {
      const expression = element.getAttribute("th:text");
      if (expression) {
        const value = fieldValue(expression, data);
        if (expression.includes("Your revised Annual CTC")) {
          const annual = (data.compensationSummary ?? appraisalCompensationSummary(data.annexureValues)).annualCtc;
          element.textContent = `Your revised Annual CTC will be Rs. ${number.format(annual)}/-. The Salary structure is detailed in Annexure I.`;
        } else if (expression.includes("'Dear '")) {
          element.textContent = `Dear ${appraisalRecipientName(data)},`;
        } else if (expression.includes("annualComponentDate")) {
          element.textContent = "* Annual Fixed component shall be paid at the completion of 1 year.";
        } else if (value !== undefined) element.textContent = value;
      }
      Array.from(element.attributes).filter((attribute) => attribute.name.startsWith("th:")).forEach((attribute) => element.removeAttribute(attribute.name));
    });

    if (includeToolbar) {
      const toolbar = documentNode.createElement("div");
      toolbar.className = "preview-toolbar";
      toolbar.innerHTML = "<button type='button' onclick='window.print()'>Print / Save as PDF</button>";
      documentNode.body.prepend(toolbar);
    }
    return `<!doctype html>${documentNode.documentElement.outerHTML}`;
}

export async function openAppraisalLetterPreview(data: AppraisalLetterPreviewData): Promise<void> {
  const preview = window.open("", "appraisal-letter-preview");
  if (!preview) throw new Error("Allow pop-ups to open the appraisal preview.");
  preview.document.write("<p style='font-family:Arial;padding:24px'>Preparing appraisal preview...</p>");
  try {
    const html = await createAppraisalLetterHtml(data, true);
    preview.document.open();
    preview.document.write(html);
    preview.document.close();
    preview.document.title = `Appraisal Letter - ${data.employeeName}`;
  } catch (error) {
    preview.close();
    throw error;
  }
}

function waitForAppraisalPagination(frame: HTMLIFrameElement): Promise<void> {
  return new Promise((resolve, reject) => {
    const frameWindow = frame.contentWindow;
    const frameDocument = frame.contentDocument;
    if (!frameWindow || !frameDocument?.body) {
      reject(new Error("Unable to access the appraisal letter renderer."));
      return;
    }

    let settled = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      window.clearInterval(poll);
      window.clearTimeout(timeout);
      frameWindow.removeEventListener("appraisal-pagination-ready", onReady);
      frameWindow.removeEventListener("appraisal-pagination-error", onError);
      if (error) reject(error); else resolve();
    };
    const inspectState = () => {
      const state = frameDocument.body.dataset.paginationReady;
      if (state === "true") finish();
      else if (state === "error") finish(new Error(
        frameDocument.body.dataset.paginationError || "The appraisal pagination script failed while laying out the letter.",
      ));
    };
    const onReady = () => finish();
    const onError = (event: Event) => {
      const message = event instanceof CustomEvent && typeof event.detail === "string"
        ? event.detail
        : "The appraisal pagination script failed while laying out the letter.";
      finish(new Error(message));
    };
    const poll = window.setInterval(inspectState, 100);
    const timeout = window.setTimeout(
      () => finish(new Error("The appraisal pagination script did not finish within 30 seconds. Verify that public/templates/appraisal-v2-dynamic.js is available in production.")),
      30000,
    );
    frameWindow.addEventListener("appraisal-pagination-ready", onReady);
    frameWindow.addEventListener("appraisal-pagination-error", onError);
    inspectState();
  });
}

export async function downloadAppraisalLetterPdf(data: AppraisalLetterPreviewData): Promise<void> {
  const A4_WIDTH_PX = 794;
  const frame = document.createElement("iframe");
  frame.title = "Appraisal PDF generator";
  frame.style.position = "fixed";
  frame.style.left = "-10000px";
  frame.style.top = "0";
  frame.style.width = `${A4_WIDTH_PX}px`;
  frame.style.height = "1123px";
  frame.style.border = "0";
  frame.style.pointerEvents = "none";
  document.body.appendChild(frame);
  try {
    const html = await createAppraisalLetterHtml(data);
    const loaded = new Promise<void>((resolve, reject) => {
      frame.addEventListener("load", () => resolve(), { once: true });
      frame.addEventListener("error", () => reject(new Error("Unable to render the appraisal letter.")), { once: true });
    });
    frame.srcdoc = html;
    await loaded;
    const frameDocument = frame.contentDocument;
    if (!frameDocument?.body) throw new Error("Unable to render the appraisal letter.");
    await waitForAppraisalPagination(frame);
    await frameDocument.fonts?.ready;
    await Promise.all(Array.from(frameDocument.images).map((image) => image.complete
      ? Promise.resolve()
      : new Promise<void>((resolve) => { image.addEventListener("load", () => resolve(), { once: true }); image.addEventListener("error", () => resolve(), { once: true }); })));
    const pages = Array.from(frameDocument.querySelectorAll<HTMLElement>(".pdf-page"));
    if (!pages.length) throw new Error("The appraisal letter contains no printable pages.");
    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait", compress: true });
    for (let index = 0; index < pages.length; index += 1) {
      const canvas = await html2canvas(pages[index], {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
        width: A4_WIDTH_PX,
        height: 1123,
        windowWidth: A4_WIDTH_PX,
        scrollX: 0,
        scrollY: 0,
      });
      if (index > 0) pdf.addPage("a4", "portrait");
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.96), "JPEG", 0, 0, 210, 297, undefined, "FAST");
    }
    pdf.save(appraisalLetterPdfFilename(data.employeeName));
  } finally {
    frame.remove();
  }
}
